import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useStore } from '../hooks/useStore';
import { can, firstAllowedRoute } from '../utils/permissions';

interface ProtectedRouteProps {
  children: React.ReactNode;
  /**
   * Permessi che sbloccano la route (basta uno; ALL passa sempre).
   * Senza prop: basta essere admin autenticato.
   */
  anyOf?: string[];
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, anyOf }) => {
  const { isAuthenticated, user, isInitializing } = useStore();
  const location = useLocation();

  // Show loading during initialization
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking authentication...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check if user has admin role
  if (user && user.userRole.roleName !== 'ADMIN') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h1>
          <p className="text-gray-600">
            You don't have permission to access this area. Admin access required.
          </p>
        </div>
      </div>
    );
  }

  // RBAC granulare: la pagina richiede uno dei permessi indicati.
  // Redirect (non pagina di errore): l'URL può arrivare da un preferito
  // salvato prima di una revoca — meglio atterrare su ciò che è permesso.
  if (anyOf && anyOf.length > 0 && !can(user, ...anyOf)) {
    // '/dashboard' come ultima spiaggia: se anche quella è vietata, il
    // RequirePerm della route mostra la schermata "nessuna sezione".
    return <Navigate to={firstAllowedRoute(user) ?? '/dashboard'} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;